<template>
  <div class="alerts-rules-tab">
    <div class="alerts-admin-board__card alerts-rules-tab__card">
      <div class="alerts-admin-board__card-head">
        <h4>Alert rules <span class="alerts-rules-tab__count">{{ rules.length }} configured</span></h4>
        <button type="button" class="alerts-admin-board__add-btn" @click="openRuleModal">New rule</button>
      </div>
      <div class="alerts-rules-tab__scroll">
        <table>
          <thead>
            <tr><th>Rule</th><th>Severity</th><th>Channels</th><th><span class="alerts-rules-tab__sr">Actions</span></th></tr>
          </thead>
          <tbody>
            <tr v-if="!rules.length"><td colspan="4" class="alerts-admin-board__empty">{{ rulesLoaded ? 'No rules configured yet.' : 'Loading rules…' }}</td></tr>
            <tr v-for="rule in rules" :key="rule.id">
              <td>
                <div class="alerts-admin-board__rule-name">{{ rule.name }}</div>
                <div class="alerts-admin-board__rule-trigger">{{ ruleTriggerSummary(rule) }} · {{ deliverySummary(rule) }}</div>
              </td>
              <td><AlertSeverityPill :severity="rule.severity" /></td>
              <td>{{ channelCountLabel(rule) }}</td>
              <td class="alerts-admin-board__rule-actions">
                <button type="button" class="alerts-admin-board__link-btn" @click="openRuleEditModal(rule)">Edit</button>
                <button
                  type="button"
                  class="alerts-admin-board__toggle"
                  :class="{ 'alerts-admin-board__toggle--off': !rule.enabled }"
                  :aria-pressed="rule.enabled ? 'true' : 'false'"
                  :aria-label="`${rule.name}: ${rule.enabled ? 'on — evaluating' : 'off — not evaluating'}`"
                  @click="toggleRule(rule)"
                >{{ rule.enabled ? 'On' : 'Off' }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="alerts-admin-board__card alerts-rules-tab__card">
      <div class="alerts-admin-board__card-head">
        <h4>Notification channels</h4>
        <button type="button" class="alerts-admin-board__add-btn" @click="openChannelModal">Add channel</button>
      </div>
      <div v-if="!channels.length" class="alerts-admin-board__empty">{{ channelsLoaded ? 'No channels configured yet.' : 'Loading channels…' }}</div>
      <div v-for="channel in channels" :key="channel.id" class="alerts-admin-board__channel-row">
        <div class="alerts-admin-board__channel-info">
          <div class="alerts-admin-board__channel-name">{{ channel.name }}</div>
          <div class="alerts-admin-board__channel-dest">{{ channelDest(channel) }}</div>
          <div class="alerts-admin-board__channel-digest">{{ channelDigestLine(channel) }}</div>
        </div>
        <span class="alerts-admin-board__verify-pill" :class="channel.verification_status === 'verified' ? 'alerts-admin-board__verify-pill--ok' : 'alerts-admin-board__verify-pill--pending'">
          {{ channel.verification_status === 'verified' ? 'Verified' : 'Pending' }}
        </span>
        <button type="button" class="alerts-admin-board__link-btn" @click="openChannelEditModal(channel)">Edit</button>
        <button type="button" class="alerts-admin-board__link-btn" @click="testChannel(channel)">{{ testingId === channel.id ? 'Testing…' : 'Test' }}</button>
      </div>
    </div>

    <AlertsDialog v-if="ruleModal.open" title="New alert rule" @close="closeRuleModal">
      <div class="alerts-admin-board__form">
        <label>Name<input v-model="ruleForm.name" type="text" placeholder="e.g. Disk usage &gt; 80%"></label>
        <label>Type
          <select v-model="ruleForm.type">
            <option value="metric_threshold">Metric threshold</option>
            <option value="check_state">Check-state change</option>
            <option value="heartbeat_missed">Heartbeat missed</option>
          </select>
        </label>
        <label>Severity
          <select v-model="ruleForm.severity">
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="serious">Serious</option>
            <option value="critical">Critical</option>
          </select>
        </label>
        <template v-if="ruleForm.type === 'metric_threshold'">
          <label>Metric<input v-model="ruleForm.config.metric" type="text" placeholder="disk_pct | mem_pct | load"></label>
          <label>Operator
            <select v-model="ruleForm.config.operator">
              <option value=">">&gt;</option>
              <option value=">=">&gt;=</option>
              <option value="<">&lt;</option>
              <option value="<=">&lt;=</option>
            </select>
          </label>
          <label>Threshold value<input v-model.number="ruleForm.config.value" type="number" step="0.1"></label>
          <label>Consecutive samples<input v-model.number="ruleForm.config.duration_samples" type="number" min="1"></label>
        </template>
        <template v-else-if="ruleForm.type === 'check_state'">
          <label>Unit filter (blank = any)<input v-model="ruleForm.config.unit_filter" type="text" placeholder="nginx"></label>
          <label>Consecutive failures<input v-model.number="ruleForm.config.consecutive" type="number" min="1"></label>
        </template>
        <template v-else>
          <label>Miss threshold<input v-model.number="ruleForm.config.miss_threshold" type="number" min="1"></label>
        </template>
        <label class="alerts-admin-board__checkbox-row"><input v-model="ruleForm.enabled" type="checkbox"> Enabled</label>
        <div v-if="ruleModal.error" class="alerts-admin-board__modal-error" role="alert">{{ ruleModal.error }}</div>
      </div>
      <template #actions>
        <button type="button" class="alerts-admin-board__mini-btn" @click="closeRuleModal">Cancel</button>
        <button type="button" class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--primary" @click="submitRule">Create rule</button>
      </template>
    </AlertsDialog>

    <!-- Plan 012 §14 — edit an EXISTING rule's routing. The On/Off toggle in
         the table is unchanged and is NOT the way to quieten a rule — Off
         stops it evaluating. -->
    <AlertsDialog
      v-if="editModal.open"
      title="Edit alert rule"
      :subtitle="`${editModal.rule?.name} · ${ruleTypeLabel(editModal.rule?.type)}`"
      wide
      @close="closeRuleEditModal"
    >
      <div class="alerts-admin-board__form">
        <fieldset class="alerts-admin-board__field-group">
          <legend class="alerts-admin-board__field-label">Send to</legend>
          <div v-if="!channels.length" class="alerts-admin-board__hint">No notification channels yet — add one under "Notification channels".</div>
          <label v-for="channel in channels" :key="channel.id" class="alerts-admin-board__checkbox-row">
            <input v-model="editForm.channelIds" type="checkbox" :value="Number(channel.id)">
            {{ channel.name }} <span class="alerts-admin-board__hint-inline">· {{ channelTypeLabel(channel.type) }}{{ channel.enabled ? '' : ' · disabled' }}</span>
          </label>
        </fieldset>

        <label>Delivery
          <select v-model="editForm.deliveryMode">
            <option value="">Default ({{ deliveryModeLabel(editModal.rule?.defaults?.delivery_mode) }})</option>
            <option value="immediate">Immediate</option>
            <option value="digest">Digest</option>
            <option value="dashboard">Dashboard only</option>
          </select>
        </label>
        <div class="alerts-admin-board__hint">{{ deliveryModeHint(effectiveEditMode) }} Critical alerts always send immediately unless the rule is dashboard-only; info alerts never interrupt.</div>

        <label>Severity
          <select v-model="editForm.severity">
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="serious">Serious</option>
            <option value="critical">Critical</option>
          </select>
        </label>
        <div v-if="editModal.rule?.severity_source === 'type_default' && editForm.severity === editModal.rule?.severity" class="alerts-admin-board__hint">
          Not set on this rule — {{ editModal.rule?.severity }} is the default for this rule type. Expiry rules rise to serious at 7 days and critical at 1 day while unset.
        </div>

        <label>Repeat
          <select v-model="editForm.repeatMode">
            <option value="default">Default — by severity ({{ repeatScheduleText(editForm.severity) }})</option>
            <option value="interval">Every…</option>
            <option value="never">Never</option>
          </select>
        </label>
        <div v-if="editForm.repeatMode === 'interval'" class="alerts-admin-board__inline-fields">
          <input v-model.number="editForm.repeatValue" type="number" min="1" step="1" aria-label="Repeat interval">
          <select v-model="editForm.repeatUnit" aria-label="Repeat interval unit">
            <option value="60">minutes</option>
            <option value="3600">hours</option>
            <option value="86400">days</option>
          </select>
        </div>
        <div class="alerts-admin-board__hint">
          Repeats are replies in the alert's thread and stop when it is acknowledged.
          <template v-if="effectiveEditMode !== 'immediate'"> This rule's delivery is not immediate, so it does not repeat.</template>
        </div>

        <template v-if="effectiveEditMode === 'digest'">
          <label>Escalate if still open after
            <select v-model="editForm.escalateMode">
              <option value="never">Never</option>
              <option value="after">After…</option>
            </select>
          </label>
          <div v-if="editForm.escalateMode === 'after'" class="alerts-admin-board__inline-fields">
            <input v-model.number="editForm.escalateValue" type="number" min="1" step="1" aria-label="Escalate after">
            <select v-model="editForm.escalateUnit" aria-label="Escalate after unit">
              <option value="3600">hours</option>
              <option value="86400">days</option>
            </select>
          </div>
        </template>

        <label v-if="isExpiryType(editModal.rule?.type)">Interrupt when this many days or fewer remain
          <input v-model.number="editForm.immediateWithinDays" type="number" min="0" step="1">
        </label>

        <div class="alerts-admin-board__field-group">
          <button type="button" class="alerts-admin-board__link-btn" @click="previewRule">{{ rulePreview.loading ? 'Composing…' : 'Preview the messages' }}</button>
          <pre v-if="rulePreview.text" class="alerts-admin-board__preview">{{ rulePreview.text }}</pre>
        </div>

        <div v-if="editModal.error" class="alerts-admin-board__modal-error" role="alert">{{ editModal.error }}</div>
      </div>
      <template #actions>
        <button type="button" class="alerts-admin-board__mini-btn" @click="closeRuleEditModal">Cancel</button>
        <button type="button" class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--primary" :disabled="editModal.saving" @click="submitRuleEdit">{{ editModal.saving ? 'Saving…' : 'Save rule' }}</button>
      </template>
    </AlertsDialog>

    <AlertsDialog
      v-if="channelModal.open"
      :title="channelModal.editingId ? 'Edit notification channel' : 'New notification channel'"
      wide
      @close="closeChannelModal"
    >
      <div class="alerts-admin-board__form">
        <label>Name<input v-model="channelForm.name" type="text" placeholder="e.g. Email — ops team"></label>
        <label>Type
          <!-- Type is fixed once a channel exists — its `config` shape is
               type-specific, and swapping it mid-edit would strand whatever
               secret is already stored under the old shape. -->
          <select v-model="channelForm.type" :disabled="!!channelModal.editingId">
            <option value="email">Email</option>
            <option value="webhook">Webhook</option>
            <option value="google_chat">Google Chat</option>
          </select>
        </label>
        <label v-if="channelForm.type === 'email'">Address<input v-model="channelForm.address" type="email" placeholder="ops@example.com"></label>
        <!-- Webhook/Google Chat URL embeds a bearer credential — the API never
             returns it in full (NotificationChannel::maskConfig()); an edit
             shows the masked value with an explicit "Replace" action, and an
             untouched edit sends no `url` at all. -->
        <template v-else-if="channelModal.editingId && !channelForm.replacingUrl">
          <div class="alerts-admin-board__field-group">
            <div class="alerts-admin-board__field-label">{{ channelForm.type === 'google_chat' ? 'Google Chat webhook URL' : 'Webhook URL' }}</div>
            <div class="alerts-admin-board__secret-row">
              <span class="alerts-admin-board__secret-value">{{ channelForm.maskedUrl || (channelForm.urlConfigured ? 'Configured — hidden' : 'Not set') }}</span>
              <button type="button" class="alerts-admin-board__link-btn" @click="channelForm.replacingUrl = true">Replace</button>
            </div>
          </div>
          <label v-if="channelForm.type === 'webhook'" class="alerts-admin-board__checkbox-row"><input v-model="channelForm.slackCompatible" type="checkbox"> Slack-compatible</label>
        </template>
        <template v-else-if="channelForm.type === 'google_chat'">
          <label>Google Chat webhook URL<input v-model="channelForm.url" type="text" placeholder="https://chat.googleapis.com/v1/spaces/…/messages?key=…&amp;token=…"></label>
        </template>
        <template v-else>
          <label>Webhook URL<input v-model="channelForm.url" type="text" placeholder="https://hooks.slack.com/services/…"></label>
          <label class="alerts-admin-board__checkbox-row"><input v-model="channelForm.slackCompatible" type="checkbox"> Slack-compatible</label>
        </template>

        <fieldset class="alerts-admin-board__field-group">
          <legend class="alerts-admin-board__field-label">Routing</legend>
          <label>Lowest severity sent here
            <select v-model="channelForm.minSeverity">
              <option value="info">Info (everything)</option>
              <option value="warning">Warning</option>
              <option value="serious">Serious</option>
              <option value="critical">Critical only</option>
            </select>
          </label>
          <label class="alerts-admin-board__checkbox-row"><input v-model="channelForm.notifyResolves" type="checkbox"> Post a "resolved" reply when an alert sent here closes</label>
        </fieldset>

        <fieldset class="alerts-admin-board__field-group">
          <legend class="alerts-admin-board__field-label">Digest</legend>
          <label class="alerts-admin-board__checkbox-row"><input v-model="channelForm.digestEnabled" type="checkbox"> Send a daily digest of digest-mode alerts</label>
          <template v-if="channelForm.digestEnabled">
            <div class="alerts-admin-board__inline-fields">
              <label>Time<input v-model="channelForm.dailyAt" type="time" step="60"></label>
              <label>Timezone<input v-model="channelForm.timezone" type="text" list="alerts-admin-board-timezones" placeholder="Europe/London"></label>
              <datalist id="alerts-admin-board-timezones">
                <option v-for="zone in timezoneOptions" :key="zone" :value="zone" />
              </datalist>
            </div>
            <label>Hourly roll-up
              <select v-model="channelForm.hourlyRollup">
                <option value="">Off</option>
                <option value="critical">Critical</option>
                <option value="serious">Serious and above</option>
                <option value="warning">Warning and above</option>
              </select>
            </label>
            <label class="alerts-admin-board__checkbox-row"><input v-model="channelForm.sendWhenEmpty" type="checkbox"> Send it even on a quiet day ("nothing to report")</label>
            <div v-if="!channelForm.sendWhenEmpty && isLastDigestChannel" class="alerts-admin-board__hint alerts-admin-board__hint--warn">
              This is the only channel with a digest. With quiet-day sending off, a missing digest and a healthy estate look the same.
            </div>
            <div class="alerts-admin-board__hint">The hourly roll-up only posts when something new at that severity arrived in the hour; the daily digest is the heartbeat.</div>
          </template>
        </fieldset>

        <div v-if="channelModal.editingId" class="alerts-admin-board__field-group">
          <div class="alerts-admin-board__field-label">Preview</div>
          <div class="alerts-admin-board__inline-actions">
            <button type="button" class="alerts-admin-board__link-btn" @click="previewDigest">{{ digestPreview.loading ? 'Composing…' : 'Show the digest this channel would get now' }}</button>
            <button type="button" class="alerts-admin-board__link-btn" @click="sendTestDigest">{{ digestPreview.sending ? 'Sending…' : 'Send test digest now' }}</button>
          </div>
          <div v-if="digestPreview.note" class="alerts-admin-board__hint" role="status">{{ digestPreview.note }}</div>
          <pre v-if="digestPreview.text" class="alerts-admin-board__preview">{{ digestPreview.text }}</pre>
        </div>

        <div v-if="channelModal.error" class="alerts-admin-board__modal-error" role="alert">{{ channelModal.error }}</div>
      </div>
      <template #actions>
        <button type="button" class="alerts-admin-board__mini-btn" @click="closeChannelModal">Cancel</button>
        <button type="button" class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--primary" @click="submitChannel">{{ channelModal.editingId ? 'Save channel' : 'Create channel' }}</button>
      </template>
    </AlertsDialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import AlertSeverityPill from '@root/components/Alerts/AlertSeverityPill.vue';
import AlertsDialog from '@root/components/Alerts/AlertsDialog.vue';
import {
  deliveryModeHint,
  deliveryModeLabel,
  parseMaybeJson,
  ruleTriggerSummary,
  ruleTypeLabel,
} from '@root/services/alertFormat';

// The Rules & channels tab (design brief §2.6): the rules table and channel
// list that used to sit in the right-hand column, unchanged in content and
// behaviour — On/Off toggle, Edit modals, previews, "Send test digest now".
// The four modals now use the workspace's dialog shell (AlertsDialog:
// role="dialog", Escape, focus trap, first field focused, focus returned) —
// the backlog's "Edit-rule modal accessibility" item.
const props = defineProps({
  apiBase: { type: String, required: true },
});
const emit = defineEmits(['changed']);

const api = useApi();
const rules = ref([]);
const channels = ref([]);
const rulesLoaded = ref(false);
const channelsLoaded = ref(false);
const testingId = ref(null);
const repeatSchedule = ref({});
const EXPIRY_TYPES = ['domain_expiration', 'agent_cert_expiring', 'site_tls_expiring'];

const editModal = reactive({ open: false, rule: null, saving: false, error: null });
const editForm = reactive({
  channelIds: [], deliveryMode: '', severity: 'warning', repeatMode: 'default', repeatValue: 1, repeatUnit: '3600',
  escalateMode: 'never', escalateValue: 1, escalateUnit: '86400', immediateWithinDays: 7,
});
const rulePreview = reactive({ loading: false, text: '' });

const ruleModal = reactive({ open: false, error: null });
const ruleForm = reactive({
  name: '',
  type: 'metric_threshold',
  severity: 'warning',
  enabled: true,
  config: { metric: 'disk_pct', operator: '>', value: 80, duration_samples: 1, unit_filter: '', consecutive: 1, miss_threshold: 3 },
});

const channelModal = reactive({ open: false, editingId: null, error: null });
const channelForm = reactive({
  name: '', type: 'email', address: '', url: '', slackCompatible: false, maskedUrl: '', urlConfigured: false, replacingUrl: false,
  minSeverity: 'info', notifyResolves: true, digestEnabled: true, dailyAt: '08:00', timezone: 'Europe/London', hourlyRollup: '', sendWhenEmpty: true,
});
const digestPreview = reactive({ loading: false, sending: false, text: '', note: '' });
const timezoneOptions = ['Europe/London', 'Europe/Dublin', 'Europe/Paris', 'Europe/Berlin', 'UTC', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Asia/Singapore', 'Australia/Sydney'];

const isLastDigestChannel = computed(() => !channels.value.some((c) => (
  c.id !== channelModal.editingId && c.enabled && c.routing?.digest?.enabled
)));

async function loadRules() {
  try {
    const data = await api.get(`${props.apiBase}/rules`);
    rules.value = (data.rules || []).map((r) => ({ ...r, config: parseMaybeJson(r.config, {}), channel_ids: parseMaybeJson(r.channel_ids, []) }));
    repeatSchedule.value = data.repeat_schedule || {};
  } catch {
    rules.value = [];
  } finally {
    rulesLoaded.value = true;
  }
}

async function loadChannels() {
  try {
    const data = await api.get(`${props.apiBase}/channels`);
    channels.value = (data.channels || []).map((c) => ({ ...c, config: parseMaybeJson(c.config, {}) }));
  } catch {
    channels.value = [];
  } finally {
    channelsLoaded.value = true;
  }
}

async function changed() {
  emit('changed');
}

async function toggleRule(rule) {
  try {
    await api.put(`${props.apiBase}/rules/${rule.id}`, { enabled: !rule.enabled });
    await loadRules();
    changed();
  } catch { /* row stays as it was; the next load shows the truth */ }
}

function channelCountLabel(rule) {
  const ids = Array.isArray(rule.channel_ids) ? rule.channel_ids : [];
  return ids.length ? `${ids.length} channel${ids.length === 1 ? '' : 's'}` : 'none';
}

function isExpiryType(type) {
  return EXPIRY_TYPES.includes(type);
}

/** "last digest: 08:00 today · sent" (§7.6), or what the channel is set to. */
function channelDigestLine(channel) {
  const digest = channel.routing?.digest;
  if (!digest || !digest.enabled) {
    return 'no digest';
  }
  const last = channel.last_digest;
  const schedule = `digest ${digest.daily_at} ${digest.timezone}${digest.hourly_rollup_min_severity ? ` · hourly ${digest.hourly_rollup_min_severity}+` : ''}`;
  if (!last) {
    return `${schedule} · none sent yet`;
  }
  const when = new Date(last.scheduled_for * 1000);
  const today = new Date();
  const sameDay = when.toDateString() === today.toDateString();
  const time = when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const outcome = { sent: 'sent', failed: 'failed', sending: 'sending', skipped_empty: 'quiet day, not sent' }[last.outcome] || last.outcome;
  return `last digest: ${time} ${sameDay ? 'today' : when.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} · ${outcome}`;
}

function channelTypeLabel(type) {
  return { email: 'Email', webhook: 'Webhook', google_chat: 'Google Chat' }[type] || type;
}

function effectiveDeliveryMode(rule) {
  return rule?.delivery_mode || rule?.defaults?.delivery_mode || 'immediate';
}

function deliverySummary(rule) {
  const mode = deliveryModeLabel(effectiveDeliveryMode(rule)).toLowerCase();
  return rule?.delivery_mode ? mode : `${mode} (default)`;
}

function repeatScheduleText(severity) {
  return repeatSchedule.value?.[severity] || 'by severity';
}

const effectiveEditMode = computed(() => editForm.deliveryMode || editModal.rule?.defaults?.delivery_mode || 'immediate');

function openRuleEditModal(rule) {
  editModal.rule = rule;
  editModal.error = null;
  editModal.saving = false;
  editForm.channelIds = (Array.isArray(rule.channel_ids) ? rule.channel_ids : []).map((id) => Number(id));
  editForm.deliveryMode = rule.delivery_mode || '';
  editForm.severity = rule.severity || 'warning';
  const interval = rule.renotify_interval_seconds;
  if (interval === null || interval === undefined || interval === '') {
    editForm.repeatMode = 'default';
    editForm.repeatValue = 1;
    editForm.repeatUnit = '3600';
  } else if (Number(interval) === 0) {
    editForm.repeatMode = 'never';
  } else {
    const seconds = Number(interval);
    const unit = seconds % 86400 === 0 ? 86400 : (seconds % 3600 === 0 ? 3600 : 60);
    editForm.repeatMode = 'interval';
    editForm.repeatUnit = String(unit);
    editForm.repeatValue = Math.max(1, Math.round(seconds / unit));
  }
  const after = Number(rule.escalate_after_seconds || 0);
  if (after > 0) {
    editForm.escalateMode = 'after';
    editForm.escalateUnit = after % 86400 === 0 ? '86400' : '3600';
    editForm.escalateValue = Math.max(1, Math.round(after / Number(editForm.escalateUnit)));
  } else {
    editForm.escalateMode = 'never';
    editForm.escalateValue = 1;
    editForm.escalateUnit = '86400';
  }
  const config = parseMaybeJson(rule.config, {}) || {};
  editForm.immediateWithinDays = Number.isFinite(Number(config.immediate_within_days)) && config.immediate_within_days !== undefined
    ? Number(config.immediate_within_days)
    : 7;
  rulePreview.text = '';
  editModal.open = true;
}

async function previewRule() {
  const rule = editModal.rule;
  if (!rule) return;
  rulePreview.loading = true;
  try {
    const data = await api.post(`${props.apiBase}/preview`, { rule_id: rule.id });
    const p = data.preview || {};
    rulePreview.text = [
      p.sample ? '(no alert from this rule yet — a sample)' : `(from alert #${p.alert_id})`,
      `Opens:    ${p.open}`,
      `Repeats:  ${p.repeat}`,
      `Resolves: ${p.resolve}`,
    ].join('\n');
  } catch {
    rulePreview.text = 'Could not compose a preview.';
  } finally {
    rulePreview.loading = false;
  }
}

function closeRuleEditModal() {
  editModal.open = false;
  editModal.rule = null;
}

async function submitRuleEdit() {
  const rule = editModal.rule;
  if (!rule) return;

  let renotify = null;
  if (editForm.repeatMode === 'never') {
    renotify = 0;
  } else if (editForm.repeatMode === 'interval') {
    renotify = Math.round(Number(editForm.repeatValue) * Number(editForm.repeatUnit));
    if (!Number.isFinite(renotify) || renotify < 300) {
      editModal.error = 'A fixed repeat must be at least 5 minutes.';
      return;
    }
  }

  let escalateAfter = null;
  if (effectiveEditMode.value === 'digest' && editForm.escalateMode === 'after') {
    escalateAfter = Math.round(Number(editForm.escalateValue) * Number(editForm.escalateUnit));
    if (!Number.isFinite(escalateAfter) || escalateAfter < 300) {
      editModal.error = 'Escalation needs a whole number of hours or days.';
      return;
    }
  }

  const payload = {
    channel_ids: editForm.channelIds.map((id) => Number(id)),
    delivery_mode: editForm.deliveryMode || null,
    renotify_interval_seconds: renotify,
    escalate_after_seconds: escalateAfter,
  };
  if (isExpiryType(rule.type)) {
    const within = Math.round(Number(editForm.immediateWithinDays));
    if (!Number.isFinite(within) || within < 0) {
      editModal.error = 'The interrupt threshold must be a whole number of days.';
      return;
    }
    payload.config = { ...(parseMaybeJson(rule.config, {}) || {}), immediate_within_days: within };
  }
  // Severity is a required taxonomy tag on the server: an untouched
  // untagged rule stays untagged (and keeps following its type default).
  if (editForm.severity !== rule.severity || rule.severity_source === 'tagged') {
    payload.severity = editForm.severity;
  }

  editModal.saving = true;
  editModal.error = null;
  try {
    await api.put(`${props.apiBase}/rules/${rule.id}`, payload);
    closeRuleEditModal();
    await loadRules();
    changed();
  } catch (err) {
    editModal.error = err?.response?.data?.error || err?.data?.error || err?.error || 'Could not save the rule — check the values and try again.';
  } finally {
    editModal.saving = false;
  }
}

function channelDest(channel) {
  const config = channel.config || {};
  if (channel.type === 'email') {
    return config.address || '—';
  }
  // `config.url` is already the API's masked value — never the real webhook URL.
  return config.url || (channel.has_secret ? 'Configured — hidden' : '—');
}

function openRuleModal() {
  ruleModal.error = null;
  ruleModal.open = true;
}
function closeRuleModal() {
  ruleModal.open = false;
}

async function submitRule() {
  const config = ruleForm.type === 'metric_threshold'
    ? { metric: ruleForm.config.metric, operator: ruleForm.config.operator, value: Number(ruleForm.config.value), duration_samples: Number(ruleForm.config.duration_samples) }
    : ruleForm.type === 'check_state'
      ? { unit_filter: ruleForm.config.unit_filter, consecutive: Number(ruleForm.config.consecutive) }
      : { miss_threshold: Number(ruleForm.config.miss_threshold) };

  try {
    await api.post(`${props.apiBase}/rules`, {
      name: ruleForm.name,
      type: ruleForm.type,
      severity: ruleForm.severity,
      enabled: ruleForm.enabled,
      config,
      server_scope: 'all',
      channel_ids: [],
    });
    closeRuleModal();
    await loadRules();
    changed();
  } catch (err) {
    // Validation error — the dialog stays open for correction, and now says why.
    ruleModal.error = err?.response?.data?.error || 'Could not create the rule — check the values and try again.';
  }
}

function resetChannelForm() {
  channelForm.name = '';
  channelForm.type = 'email';
  channelForm.address = '';
  channelForm.url = '';
  channelForm.slackCompatible = false;
  channelForm.maskedUrl = '';
  channelForm.urlConfigured = false;
  channelForm.replacingUrl = false;
  applyRoutingToForm(null);
  digestPreview.text = '';
  digestPreview.note = '';
  channelModal.error = null;
}

function applyRoutingToForm(routing) {
  const digest = routing?.digest || {};
  channelForm.minSeverity = routing?.min_severity || 'info';
  channelForm.notifyResolves = routing ? routing.notify_resolves !== false : true;
  channelForm.digestEnabled = routing ? digest.enabled !== false : true;
  channelForm.dailyAt = digest.daily_at || '08:00';
  channelForm.timezone = digest.timezone || 'Europe/London';
  channelForm.hourlyRollup = routing ? (digest.hourly_rollup_min_severity || '') : '';
  channelForm.sendWhenEmpty = routing ? digest.send_when_empty !== false : true;
}

function routingConfigFromForm() {
  return {
    min_severity: channelForm.minSeverity,
    notify_resolves: !!channelForm.notifyResolves,
    digest: {
      enabled: !!channelForm.digestEnabled,
      daily_at: (channelForm.dailyAt || '08:00').slice(0, 5),
      timezone: (channelForm.timezone || '').trim() || 'Europe/London',
      hourly_rollup_min_severity: channelForm.hourlyRollup || null,
      send_when_empty: !!channelForm.sendWhenEmpty,
    },
  };
}

function openChannelModal() {
  resetChannelForm();
  channelModal.editingId = null;
  channelModal.open = true;
}

function openChannelEditModal(channel) {
  resetChannelForm();
  channelModal.editingId = channel.id;
  channelModal.open = true;
  channelForm.name = channel.name;
  channelForm.type = channel.type;
  const config = channel.config || {};
  if (channel.type === 'email') {
    channelForm.address = config.address || '';
  } else {
    channelForm.maskedUrl = config.url || '';
    channelForm.urlConfigured = !!channel.has_secret;
    channelForm.slackCompatible = !!config.slack_compatible;
  }
  applyRoutingToForm(channel.routing || null);
}

async function previewDigest() {
  if (!channelModal.editingId) return;
  digestPreview.loading = true;
  digestPreview.note = '';
  try {
    const data = await api.post(`${props.apiBase}/preview`, { channel_id: channelModal.editingId });
    digestPreview.text = data.preview?.text || '';
    digestPreview.note = 'The last 24 hours, with this channel\'s SAVED settings — save first to preview changes.';
  } catch {
    digestPreview.note = 'Could not compose the preview.';
  } finally {
    digestPreview.loading = false;
  }
}

async function sendTestDigest() {
  if (!channelModal.editingId) return;
  digestPreview.sending = true;
  try {
    const data = await api.post(`${props.apiBase}/channels/${channelModal.editingId}/test-digest`);
    digestPreview.note = data.success ? 'Test digest sent.' : `Not sent: ${data.error || 'unknown error'}`;
  } catch {
    digestPreview.note = 'Could not send the test digest.';
  } finally {
    digestPreview.sending = false;
  }
}

function closeChannelModal() {
  channelModal.open = false;
  channelModal.editingId = null;
}

async function submitChannel() {
  const isEdit = channelModal.editingId !== null;
  // Only include `url` when the admin actually typed a new one (create, or
  // edit + "Replace") — see the template comment.
  const includeUrl = !isEdit || channelForm.replacingUrl;
  const config = channelForm.type === 'email'
    ? { address: channelForm.address }
    : channelForm.type === 'google_chat'
      ? (includeUrl ? { url: channelForm.url } : {})
      : { ...(includeUrl ? { url: channelForm.url } : {}), slack_compatible: channelForm.slackCompatible };

  const routingConfig = routingConfigFromForm();
  channelModal.error = null;
  try {
    if (isEdit) {
      await api.put(`${props.apiBase}/channels/${channelModal.editingId}`, { name: channelForm.name, config, routing_config: routingConfig });
    } else {
      await api.post(`${props.apiBase}/channels`, { name: channelForm.name, type: channelForm.type, config, routing_config: routingConfig });
    }
    closeChannelModal();
    await loadChannels();
    changed();
  } catch (err) {
    channelModal.error = err?.response?.data?.error || err?.data?.error || err?.error || 'Could not save the channel — check the values and try again.';
  }
}

async function testChannel(channel) {
  testingId.value = channel.id;
  try {
    await api.post(`${props.apiBase}/channels/${channel.id}/test`);
    await loadChannels();
    changed();
  } finally {
    testingId.value = null;
  }
}

onMounted(() => {
  loadRules();
  loadChannels();
});
</script>

<style lang="scss">
.alerts-rules-tab {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 16px;
  padding: 16px;
  align-items: start;

  &__card {
    min-width: 0;
  }

  &__count {
    font-weight: 400;
    font-size: 11.5px;
    color: var(--theme-text-tertiary);
    margin-left: 6px;
  }

  &__scroll {
    overflow-x: auto;
  }

  &__sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    padding: 12px;
  }
}
</style>
